import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BasePopup } from "components/pop-up/BasePopup";
import ProsecutorCard from "components/card/ProsecutorCard";
import { useTooltip } from "context/TooltipProvider";
import { fetchProsecutors } from "services/assetRecoveryService";
import { getAvatarBgColor } from "utils/helper";
import {
  AppealRequestForwardingStatusEnum,
  AssetAppeal,
  AssetAppealRequestActionStatusEnum,
  LoginAccount,
} from "types";
import {
  updateAssetAppeal,
  updateRespondentAssetAppeal,
} from "services/assetAppealService";
import { useAuth } from "context/AuthContext";

interface AssetAppealProsecutorReassignPopupProps {
  isOpen: boolean;
  onClose: () => void;
  appeal: AssetAppeal;
  assignedProsecutor?: string;
  onProsecutorUpdated?: (response: any) => void;
}

interface Prosecutor {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
  };
}

export const AssetAppealProsecutorReassignPopup: React.FC<
  AssetAppealProsecutorReassignPopupProps
> = ({ isOpen, onClose, appeal, assignedProsecutor, onProsecutorUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [selectedProsecutor, setSelectedProsecutor] = useState<string>("");
  const [prosecutors, setProsecutors] = useState<Prosecutor[]>([]);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  // Check if there's already an assigned prosecutor
  const hasExistingProsecutor = Boolean(
    assignedProsecutor ||
    appeal?.assigned_prosecutor_id ||
    appeal?.assigned_prosecutor_name,
  );

  useEffect(() => {
    if (isOpen) {
      // Fetch prosecutors when popup opens
      fetchProsecutors(loginData as LoginAccount)
        .then(setProsecutors)
        .catch((error) => {
          console.error("Error fetching prosecutors:", error);
          showTooltip(
            t("failed_to_load_prosecutors", {
              defaultValue: "Failed to load prosecutors",
            }),
            "error",
          );
        });

      // Set initial selected prosecutor
      if (assignedProsecutor) {
        setSelectedProsecutor(assignedProsecutor);
      } else if (appeal?.assigned_prosecutor_id) {
        setSelectedProsecutor(appeal.assigned_prosecutor_id);
      } else {
        setSelectedProsecutor(""); // No prosecutor assigned initially
      }
    }
  }, [isOpen, assignedProsecutor, appeal]);

  const handleProsecutorChange = (value: string) => {
    setSelectedProsecutor(value);
  };
  const { loginData } = useAuth();

  const getInitials = (name: string): string => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleAssignReAssign = async () => {
    if (!selectedProsecutor) {
      showTooltip(t("please_select_prosecutor"), "error");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      // Update assigned prosecutor if changed or if it's a new assignment
      const selectedProsecutorData = prosecutors.find(
        (pros) => pros?.user?.id === selectedProsecutor,
      );

      if (selectedProsecutorData) {
        const fullName = `${selectedProsecutorData.user?.first_name} ${selectedProsecutorData.user?.last_name}`;
        formData.append("assigned_prosecutor_name", fullName);
        formData.append("assigned_prosecutor_id", selectedProsecutor);

        formData.append(
          "forwarding_status",
          AppealRequestForwardingStatusEnum.ASSIGNED_TO_PROSECUTOR,
        );
      }
      let response;
      if (appeal?.appeal_type === "appeal_by_respondant") {
        formData.append("org_unit", loginData?.org_unit.toString() as string);

        response = await updateRespondentAssetAppeal(appeal?.id, formData);
      } else {
        formData.append(
          "action_status",
          AssetAppealRequestActionStatusEnum.APPEAL_ON_LITIGATION,
        );
        response = await updateAssetAppeal(appeal?.id, formData);
      }

      if (hasExistingProsecutor) {
        showTooltip(
          t("successfully_reassigned", {
            defaultValue: "Successfully Reassigned",
          }),
          "success",
        );
      } else {
        showTooltip(t("successfully_assigned"), "success");
      }

      if (onProsecutorUpdated) {
        if (appeal?.appeal_type === "appeal_by_respondant") {
          onProsecutorUpdated(response.respondant ?? response);
        } else onProsecutorUpdated(response.appeal);
      }

      onClose();
    } catch (error) {
      console.error("Error assigning prosecutor:", error);
      if (hasExistingProsecutor) {
        showTooltip(
          t("failed_to_reassign_prosecutor", {
            defaultValue: "Failed to reassign prosecutor",
          }),
          "error",
        );
      } else {
        showTooltip(t("failed_to_assign_prosecutor"), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProsecutor(
      assignedProsecutor || appeal?.assigned_prosecutor_id || "",
    );
    onClose();
  };

  // Get initials and background color for the card
  let initials = "";
  let bgColor = "#6B7280";

  if (selectedProsecutor) {
    const selectedProsecutorData = prosecutors.find(
      (pros) => pros?.user?.id === selectedProsecutor,
    );
    if (selectedProsecutorData) {
      const fullName = `${selectedProsecutorData.user?.first_name} ${selectedProsecutorData.user?.last_name}`;
      initials = getInitials(fullName);
      bgColor = getAvatarBgColor(fullName);
    }
  } else if (appeal?.assigned_prosecutor_name) {
    initials = getInitials(appeal.assigned_prosecutor_name);
    bgColor = getAvatarBgColor(appeal.assigned_prosecutor_name);
  }

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={
        hasExistingProsecutor
          ? t("prosecutor_reassignment", {
              defaultValue: "Prosecutor Reassignment",
            })
          : t("assign_prosecutor")
      }
      description={
        hasExistingProsecutor
          ? t("reassign_prosecutor_for_case", {
              defaultValue: "Reassign prosecutor for this case",
            })
          : t("assign_prosecutor_for_case", {
              defaultValue: "Assign a prosecutor to this case",
            })
      }
    >
      <div className="relative pb-20">
        <div className="space-y-4">
          <div className="flex flex-col">
            <label className="report-popup-label">
              {t("select_prosecutor")} *
            </label>
            <select
              className="referral-form-field"
              value={selectedProsecutor}
              onChange={(e) => handleProsecutorChange(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">{t("select_prosecutor")}</option>
              {prosecutors?.map((pros) => (
                <option value={pros?.user?.id} key={pros?.user?.id}>
                  {pros.user?.first_name} {pros.user?.last_name}
                </option>
              ))}
            </select>
          </div>

          {(appeal?.assigned_prosecutor_name || selectedProsecutor) && (
            <ProsecutorCard
              selectedProsecutor={selectedProsecutor}
              prosecutors={prosecutors}
              appeal={appeal}
            />
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex justify-end space-x-3 pt-4 bg-white border-t border-gray-200 p-4 -mx-4 -mb-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            onClick={handleAssignReAssign}
            disabled={loading || !selectedProsecutor}
            className={`appeal-form-button text-white px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              loading || !selectedProsecutor
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {loading
              ? hasExistingProsecutor
                ? t("reassigning", { defaultValue: "Reassigning..." })
                : t("assigning", { defaultValue: "Assigning..." })
              : hasExistingProsecutor
                ? t("reassign", { defaultValue: "Reassign" })
                : t("assign", { defaultValue: "Assign" })}
          </button>
        </div>
      </div>
    </BasePopup>
  );
};

export default AssetAppealProsecutorReassignPopup;
